import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Widget Types ---

export interface WidgetPosition {
  x: number;
  y: number;
}

export interface WidgetSize {
  width: number;
  height: number;
}

export interface DashboardWidget {
  widget_id: string;
  type: string;
  title: string;
  data_source: string;
  config: Record<string, unknown>;
  position: WidgetPosition;
  size: WidgetSize;
}

// --- Dashboard Document ---

export interface Dashboard {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  layout: string;
  widgets: DashboardWidget[];
  owner_id: string;
  is_default: boolean;
  shared_with: string[];
  branch_id: string;
  is_template: boolean;
  template_key: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDashboardDto {
  name: string;
  description?: string;
  layout?: string;
  widgets?: CreateWidgetDto[];
  is_default?: boolean;
  shared_with?: string[];
}

export interface UpdateDashboardDto {
  name: string;
  description?: string;
  layout?: string;
  widgets?: CreateWidgetDto[];
  is_default?: boolean;
  shared_with?: string[];
}

export interface CreateWidgetDto {
  type: string;
  title: string;
  data_source: string;
  config?: Record<string, unknown>;
  position?: WidgetPosition;
  size?: WidgetSize;
}

export interface DashboardFilters extends SearchParams {
  owner_id?: string;
  is_default?: string;
  is_template?: string;
}

// --- Report Definition Document ---

export interface ReportParameter {
  key: string;
  label: string;
  type: string;
  default_value: string;
  options: string[] | null;
  required: boolean;
}

export interface ReportDefinition {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  query_template: string;
  parameters: ReportParameter[];
  output_format: string;
  owner_id: string;
  category: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateReportDefinitionDto {
  name: string;
  description?: string;
  query_template: string;
  parameters?: ReportParameterDto[];
  output_format?: string;
  category?: string;
}

export interface UpdateReportDefinitionDto {
  name: string;
  description?: string;
  query_template?: string;
  parameters?: ReportParameterDto[];
  output_format?: string;
  category?: string;
}

export interface ReportParameterDto {
  key: string;
  label: string;
  type: string;
  default_value?: string;
  options?: string[];
  required: boolean;
}

export interface ReportDefinitionFilters extends SearchParams {
  category?: string;
  output_format?: string;
  owner_id?: string;
}

// --- Aggregation Response Types ---

export interface AggregationBucket {
  key: string;
  doc_count: number;
  value: number | null;
}

export interface TimeSeriesBucket {
  date: string;
  doc_count: number;
  value: number | null;
}

export interface SalesPipelineSummaryResponse {
  stage_breakdown: AggregationBucket[];
  total_open_value: number;
  win_rate: number;
  avg_deal_size: number;
  total_open_count: number;
}

export interface RevenueOverviewResponse {
  status_breakdown: AggregationBucket[];
  monthly_trend: TimeSeriesBucket[];
  total_revenue: number;
}

export interface TopCustomerDto {
  customer_id: string;
  customer_name: string;
  total_order_value: number;
  order_count: number;
}

export interface CustomerAnalyticsResponse {
  new_customer_trend: TimeSeriesBucket[];
  industry_breakdown: AggregationBucket[];
  top_customers: TopCustomerDto[];
  total_customers: number;
}

export interface ActivitySummaryResponse {
  type_breakdown: AggregationBucket[];
  status_breakdown: AggregationBucket[];
  upcoming_count: number;
  overdue_count: number;
  total_activities: number;
}

export interface FunnelStageDto {
  stage_id: string;
  stage_name: string;
  sort_order: number;
  opportunity_count: number;
  total_value: number;
  conversion_rate: number;
}

export interface PipelineFunnelResponse {
  stages: FunnelStageDto[];
  total_value: number;
  total_count: number;
}

export interface PerformerDto {
  user_id: string;
  display_name: string;
  value: number;
  count: number;
}

export interface TopPerformersResponse {
  by_revenue: PerformerDto[];
  by_activity_count: PerformerDto[];
}

export interface AggregationParams {
  date_from?: string;
  date_to?: string;
  pipeline_id?: string;
  limit?: number;
}

// --- Dashboard API ---

export function getDashboards(params: DashboardFilters = {}) {
  return apiGet<ApiResponse<PagedResponse<Dashboard>>>('/api/v1/dashboards', params);
}

export function getDashboard(id: string) {
  return apiGet<ApiResponse<Dashboard>>(`/api/v1/dashboards/${id}`);
}

export function createDashboard(data: CreateDashboardDto) {
  return apiPost<ApiResponse<Dashboard>>('/api/v1/dashboards', data);
}

export function updateDashboard(id: string, data: UpdateDashboardDto) {
  return apiPut<ApiResponse<Dashboard>>(`/api/v1/dashboards/${id}`, data);
}

export function deleteDashboard(id: string) {
  return apiDelete<ApiResponse<unknown>>(`/api/v1/dashboards/${id}`);
}

export function getDashboardTemplates() {
  return apiGet<ApiResponse<{ items: Dashboard[]; total: number }>>('/api/v1/dashboards/templates');
}

export function cloneDashboardFromTemplate(templateKey: string) {
  return apiPost<ApiResponse<Dashboard>>(`/api/v1/dashboards/from-template/${templateKey}`);
}

// --- Report Definition API ---

export function getReportDefinitions(params: ReportDefinitionFilters = {}) {
  return apiGet<ApiResponse<PagedResponse<ReportDefinition>>>('/api/v1/report-definitions', params);
}

export function getReportDefinition(id: string) {
  return apiGet<ApiResponse<ReportDefinition>>(`/api/v1/report-definitions/${id}`);
}

export function createReportDefinition(data: CreateReportDefinitionDto) {
  return apiPost<ApiResponse<ReportDefinition>>('/api/v1/report-definitions', data);
}

export function updateReportDefinition(id: string, data: UpdateReportDefinitionDto) {
  return apiPut<ApiResponse<ReportDefinition>>(`/api/v1/report-definitions/${id}`, data);
}

export function deleteReportDefinition(id: string) {
  return apiDelete<ApiResponse<unknown>>(`/api/v1/report-definitions/${id}`);
}

// --- Aggregation API ---

export function getSalesPipelineSummary(params: AggregationParams = {}) {
  return apiGet<ApiResponse<SalesPipelineSummaryResponse>>('/api/v1/aggregations/sales-pipeline-summary', params);
}

export function getRevenueOverview(params: AggregationParams = {}) {
  return apiGet<ApiResponse<RevenueOverviewResponse>>('/api/v1/aggregations/revenue-overview', params);
}

export function getCustomerAnalytics(params: AggregationParams = {}) {
  return apiGet<ApiResponse<CustomerAnalyticsResponse>>('/api/v1/aggregations/customer-analytics', params);
}

export function getActivitySummary(params: AggregationParams = {}) {
  return apiGet<ApiResponse<ActivitySummaryResponse>>('/api/v1/aggregations/activity-summary', params);
}

export function getPipelineFunnel(params: AggregationParams = {}) {
  return apiGet<ApiResponse<PipelineFunnelResponse>>('/api/v1/aggregations/pipeline-funnel', params);
}

export function getTopPerformers(params: AggregationParams = {}) {
  return apiGet<ApiResponse<TopPerformersResponse>>('/api/v1/aggregations/top-performers', params);
}
